import React, { useState } from "react";
import { Container, Row, Col, Card, Image } from "react-bootstrap";
import parse from "react-html-parser";
import { BsChevronDown } from "react-icons/bs";
import "./SellHowItWorks.css";

import { TextFormatter } from "@/components/sell/utils/TextFormatter";
import HubzuContactUsBrokerModal from "../Modal/HubzuContactUsBrokerModal";
import { formatCmsTextToHtml } from "@/utils/textParser";

const SellHowItWorks = ({ cmsData = [], type }) => {
  const [showContactUsBrokerModal, setShowContactUsBrokerModal] =
    useState(false);

  // Early validation and default values
  if (!Array.isArray(cmsData)) {
    console.warn("SellHowItWorks: cmsData should be an array");
    cmsData = [];
  }
  // Safely access the data with fallbacks
  const hubzuCmsData = cmsData.length > 0 ? cmsData[0]?.howItWorks || {} : {};
  const brokerModalData = cmsData[0]?.contactUsBrokerModal;

  // Ensure workSteps exists and has the expected structure
  const workSteps = hubzuCmsData.workSteps?.["@nodes"]
    ? hubzuCmsData.workSteps["@nodes"].reduce((acc, node) => {
        if (hubzuCmsData.workSteps[node]) {
          acc.push(hubzuCmsData.workSteps[node]);
        }
        return acc;
      }, [])
    : [];

  const handleContactUsModal = () => {
    setShowContactUsBrokerModal(!showContactUsBrokerModal);
  };

  const StepCard = ({ step }) => (
    <Card className="hwork-details-card">
      <h4 className="hwork-subtitle1">{step.id}</h4>
      <Row>
        <Col xs={12} md className="hwork-subtitle2-container">
          <h5 className="fw-bold">{step.title}</h5>
        </Col>
        <Col xs={12} md className="hwork-subtitle2-desc-container">
          <p>{step.description}</p>
        </Col>
      </Row>
    </Card>
  );

  const StepCardCommercial = ({ step }) => {
    let features = [];
    if (step["@nodes"].length > 0) {
      features = step?.features?.["@nodes"]?.map(
        (node) => step?.features?.[node]
      );
    }

    return (
      <Card className="hwork-details-card">
        {features?.length > 0 && (
          <div className="hwork-commercial-card-features">
            {features.map((item, index) => (
              <div
                className="d-flex hwork-commercial-title-container"
                key={index}
              >
                <h5 className="fw-bold hwork-commercial-title">{item.title}</h5>
                <p>-</p>
                <p className="hwork-commercial-desc">{item.description}</p>
              </div>
            ))}
          </div>
        )}
        {step?.title && (
          <h5 className="fw-bold hwork-commercial-step-title">{step.title}</h5>
        )}
        {step?.description && (
          <p className="hwork-commercial-step-description">
            {step.description}
          </p>
        )}
      </Card>
    );
  };

  const StepImage = ({ step }) => (
    <div className={`hwork-image-wrapper ${step.haveBgColor ? "with-bg" : ""}`}>
      <Image
        src={step.image}
        alt="hwork-image"
        fluid
        className={`${step.haveBgColor ? "hwork-image-bg" : "hwork-image"} ${
          type === "residential" && "hwork-image-res"
        }`}
      />
    </div>
  );

  return (
    <Container fluid className="hwork-parent-container">
      <HubzuContactUsBrokerModal
        isModalOpen={showContactUsBrokerModal}
        handleModal={handleContactUsModal}
        cmsData={brokerModalData}
      />
      <div className="hwork-title-container text-center">
        <p
          className={`hwork-title-container-main-para ${
            type === "residential" ? "hwork-title-container-main-para-res" : ""
          }`}
        >
          {parse(formatCmsTextToHtml(hubzuCmsData?.desc))}
        </p>

        <p className="hwork-title-container-main-para2">
          {hubzuCmsData?.subDesc}
          <span>
            <BsChevronDown style={{ fontWeight: "bold", fontSize: "14px" }} />
          </span>
        </p>
      </div>

      {type === "residential" && (
        <Row className="justify-content-center">
          <Col xs={12} className="hwork-content">
            <div className="d-flex">
              <h2 className="hwork-title">{hubzuCmsData?.title}</h2>
            </div>
            {Array.isArray(workSteps) &&
              workSteps.map((step) => {
                if (!step) return null;

                const flexDirection = step.reverse
                  ? "flex-md-row-reverse"
                  : "flex-md-row";

                return (
                  <div
                    key={step.id}
                    className={`hwork-card-container d-flex ${flexDirection}`}
                  >
                    <Col md={9} className="hwork-card-left-container">
                      <StepCard step={step} />
                    </Col>

                    <Col md={3} className="hwork-image-container">
                      <StepImage step={step} />
                    </Col>
                  </div>
                );
              })}
          </Col>
        </Row>
      )}

      {type === "commercial" && (
        <Row className="justify-content-center">
          <Col xs={12} className="hwork-content">
            <div className="w-100 d-flex">
              <h2 className="hwork-title hwork-title-com">{hubzuCmsData?.title}</h2>
            </div>

            {Array.isArray(workSteps) &&
              workSteps.map((step) => {
                if (!step) return null;

                const flexDirection = step.reverse
                  ? "flex-md-row-reverse"
                  : "flex-md-row";

                return (
                  <div
                    key={step.id}
                    className={`hwork-card-container d-flex ${flexDirection}`}
                  >
                    <Col md={9} className="hwork-card-left-container">
                      <StepCardCommercial step={step} />
                    </Col>

                    <Col md={3} className="hwork-image-container">
                      <StepImage step={step} />
                    </Col>
                  </div>
                );
              })}
          </Col>
        </Row>
      )}

      {type === "broker" && (
        <div className="hwork-card-parent-container">
          <div className="w-100 d-flex">
            <h2 className="hwork-title">{hubzuCmsData?.title}</h2>
          </div>

          {Array.isArray(workSteps) &&
            workSteps.map((step, index) => {
              if (!step) return null;

              const flexDirection = step.reverse
                ? "flex-md-row-reverse"
                : "flex-md-row";
              const justifyContent = step.reverse ? "flex-end" : "flex-start";

              return (
                <div
                  key={step.id || index}
                  className={`hwork-child-container d-flex ${flexDirection}`}
                >
                  <div className="hwork-desc-container">
                    <div
                      style={{
                        display: "flex",
                        justifyContent,
                      }}
                    >
                      <div className="hwork-desc-step-count">
                        <h2>Step {index + 1}</h2>
                      </div>
                    </div>
                    <div className="hwork-desc-detail-container">
                      <h5 className="fw-bold">{step?.title}</h5>

                      <p>
                        <TextFormatter
                          text={step?.description}
                          onLinkClick={handleContactUsModal}
                        />
                        {/* {step.description} */}
                      </p>
                    </div>
                  </div>
                  <div className="hwork-img-container">
                    {step.image && (
                      <Image
                        src={step.image}
                        fluid
                        className="hwork-img"
                        alt={step.title || `Step ${index + 1}`}
                      />
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </Container>
  );
};

export default SellHowItWorks;
